import { Components } from '@mui/material/styles/components'
import { Theme } from '@mui/material/styles/createTheme'
import { getFontSize, getPaddingForSize } from '../util/button'

const overrides = (
  enableUitkFocus: boolean = false
): Components<Omit<Theme, 'components'>> => {
  return {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          '&.Mui-focusVisible': {
            boxShadow:
              ownerState.variant === 'text'
                ? `${theme.palette.secondary.main} 0px 0px 0px 2px`
                : `${theme.palette.common.white} 0px 0px 0px 2px,${theme.palette.secondary.main} 0px 0px 0px 5px`,
          },
          '&:focus': enableUitkFocus && {
            boxShadow:
              ownerState.variant === 'text'
                ? `${theme.palette.secondary.main} 0px 0px 0px 2px`
                : `${theme.palette.common.white} 0px 0px 0px 2px,${theme.palette.secondary.main} 0px 0px 0px 5px`,
          },
          '&.Mui-disabled': {
            borderWidth: 0,
            boxShadow:
              ownerState.variant === 'contained'
                ? `${theme.ues.neutral['80']} 0px 0px 0px 1px`
                : 'none',
            backgroundColor: theme.ues.neutral['10'],
            color: theme.palette.text.disabled,
          },
          textTransform: 'none',
          borderRadius: theme.spacing(3.5),
          padding: getPaddingForSize({ size: ownerState.size }),
          fontSize: getFontSize({ size: ownerState.size }),
          lineHeight: getFontSize({ size: ownerState.size }),
        }),
        sizeSmall: {
          height: '32px',
          minHeight: '32px',
        },
        sizeMedium: {
          height: '40px',
          minHeight: '40px',
        },
        sizeLarge: {
          height: '48px',
          minHeight: '48px',
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            transition: '0.1s',
            boxShadow: `${theme.palette.secondary.main} 0px 0px 0px 2px`,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.primary.main}`,
          background: '#e9f1ff',
          borderRadius: '4px',
          padding: '2px 2px',
          color: theme.palette.primary.main,
          fontWeight: 700,
          fontSize: '12px',
          lineHeight: '18px',
        }),
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          const alertVariant = theme.palette[ownerState.color || 'info']
          return {
            background:
              ownerState.variant !== 'standard'
                ? alertVariant.light
                : 'transparent',
            borderColor: alertVariant.main,
            borderWidth: ownerState.variant === 'outlined' ? 1 : 0,
            borderStyle: 'solid',
          }
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible svg': {
            outline: `auto 2px`,
            outlineOffset: '1px',
            transition: '0.1s',
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          '&.Mui-focusVisible > svg': {
            outline: `auto 2px`,
            outlineOffset: '1px',
            transition: '0.1s',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        color: 'primary',
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focusVisible': {
            boxShadow: `${theme.palette.common.white} 0px 0px 0px 2px,${theme.palette.primary.main} 0px 0px 0px 5px`,
            background: 'inherit',
            transition: '0.1s',
          },
        }),
      },
    },
    MuiLink: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          cursor: 'pointer',
          textDecorationColor: 'inherit',
          '&:hover': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
            textDecorationColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiCardActionArea: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        focusHighlight: ({ theme }) => ({
          background: theme.palette.common.white,
        }),
        focusVisible: ({ theme }) => ({
          outline: `1px solid ${theme.palette.primary.main}`,
          background: theme.palette.common.white,
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body3: 'h6',
        },
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        role: 'presentation',
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltipPlacementTop: {
          '@media (max-width:768px)': {
            marginBottom: '0px !important',
          },
        },
        popper: {
          '@media (max-width:768px)': {
            marginBottom: '-20px !important',
          },
        },
      },
    },
  }
}

export default overrides
